import { pv, sum, valueIsUndefined,infAdjReturn} from '@master/model/kyc-form/need-analysis/math'
import { IProjectSettings } from '@master/config/IProjectSettings'
import { isUndefined ,isNil, isEmpty as isLodashEmpty } from 'lodash/fp'
import { Ignore } from '@master/annotation/Ignore'
import NeedsBase from '@master/model/kyc-form/need-analysis/NeedsBase'
import KYCClient from '@company-common/model/kyc-form/KYCClient'
import { Global, NA_ERROR_MSG } from '../utils/constants'
import { isEmpty } from 'class-validator'
import { TenantSettings } from '@master/model/kyc-form/TenantSettings';
// import validateIsComplete from '../utils/validateComplete'
// import { CriticalIllnessStructure } from './CriticalIllnessStructure'

export default class CriticalIllnessModel extends NeedsBase {
  @Ignore()
  projectSettings: IProjectSettings

  selectedClient?:  KYCClient

  @Ignore()
  tenantSettings?: TenantSettings;

  name = 'Critical Illness'
  needs = 'Critical'
  isProtection = true
  additionalNotes = ''
  CRITICAL_ILLNESS_YEARS_REQ = Global.CRITICAL_ILLNESS_YEARS_REQ
  ASSUMED_INFLATION_RATE = Global.ASSUMED_INFLATION_RATE

  // Critical Illness Needs Section
  incomeMonthly?: number = undefined
  cacheIncomeMonthly?: number = undefined

  yearsRequired: number = undefined//this.CRITICAL_ILLNESS_YEARS_REQ
  cachedYearsRequired?: number = undefined

  incomeInvRate?: number = undefined
  cachedIncomeInvRate?: number = undefined

  // initial value to undefined for SSP-4815
  incomeAssumedInfRate?: number = undefined//this.ASSUMED_INFLATION_RATE
  cachedIncomeAssumedInfRate?: number = this.incomeAssumedInfRate

  infAdjReturn?: number = undefined
  lumpSumRequired?:number = undefined
  criticalOtherDesc = ''
  criticalOtherAmmount?:number = undefined

  // Existing Resources Allocated (Current Values) Section
  exResourcesLIC?:number = undefined
  exResourcesCA?:number = undefined
  cachedExResourcesCA?:number = undefined
  cachedxResourcesLIC?:number = undefined

  exResourcesOthersDesc? = ''
  exResourcesOthersAmt?:number = undefined

  // Justification model
  incomeMonthlyReason?: string = undefined
  yearsRequiredReason?: string = undefined
  incomeInvRateReason?: string = undefined
  incomeAssumedInfRateReason?: string = undefined
  incomeInfAdjRateReason?:string = undefined
  exResourcesLICReason?:string = undefined
  exResourcesCAReason?:string = undefined

  cachedIncomeInfAdjRate?: number = undefined

  // confirm review changes
  incomeMonthlyCR?:boolean = undefined
  yearsRequiredCR?:boolean = undefined
  incomeInvRateCR?:boolean = undefined
  incomeAssumedInfRateCR?:boolean = undefined
  incomeInfAdjRateCR?:boolean = undefined
  exResourcesLICCR?:boolean = undefined
  exResourcesCACR?:boolean = undefined

  cachedExResourcesCAAuto?: number = undefined
  
  // PBB-4492 - For checking if user need to revisit the needs objective
  needRevisit = false;

  constructor (type: 'client_0' | 'client_1' = 'client_0', projectSettings: IProjectSettings, id = 'p1') {
    super(`${type}_ci_${id}`)
    this.projectSettings = projectSettings
  }
  
  setTenantSettings(tenantSettings?: TenantSettings) {
    this.tenantSettings = tenantSettings;
  }
  
  set setSelectedClient (client) {
    this.selectedClient = client
  }

  setupDefaultRate (rate: number) {
    super.setupDefaultRate(rate)
    if (isUndefined(this.infAdjReturn)) this.infAdjReturn = this.defaultRate
  }

  get incomeAnnual () {
    if (valueIsUndefined(this.incomeMonthly)) return undefined
    return (12 * this.incomeMonthly) || undefined
  }

  get incomeInfAdjRate(){
    if (valueIsUndefined(this.incomeInvRate) && valueIsUndefined(this.incomeAssumedInfRate)) return undefined
    const rate = infAdjReturn(this.incomeInvRate ,this.incomeAssumedInfRate) || 0
    return parseFloat(rate.toFixed(2))
  }

  get fundsRequired () {
    if (valueIsUndefined(this.incomeAnnual) && valueIsUndefined(this.yearsRequired)) return undefined
    const total = pv(this.incomeAnnual, this.yearsRequired, this.incomeInfAdjRate)
    return total ? parseFloat(total.toFixed(2)) : undefined
  }

  get criticalIllnessTotal() {
    const total = sum(this.fundsRequired, this.lumpSumRequired, this.criticalOtherAmmount) || 0
    return parseFloat(total.toFixed(2))
  }

  get exResourcesTotal() {
    const total = sum(this.exResourcesLIC, this.exResourcesCA, this.exResourcesOthersAmt) || 0
    return parseFloat(total.toFixed(2))
  }

  get grandTotal () {
    const total = this.exResourcesTotal - this.criticalIllnessTotal
    return parseFloat(total.toFixed(2))
  }

  get isOtherFieldsValidated() {

    const isField1Filled = !isLodashEmpty(this.criticalOtherDesc) ;
    const isField2Filled = !isNil(this.criticalOtherAmmount);

    return isField1Filled === isField2Filled;
  }

  get isResourcesOtherFieldsValidated() {

    const isField1Filled = !isLodashEmpty(this.exResourcesOthersDesc) ;
    const isField2Filled = !isNil(this.exResourcesOthersAmt);

    return isField1Filled === isField2Filled;
  }

  get isCompleted() {
    const validateCINeed = this.tenantSettings?.tenantValidateCINeed ?? false;

    // Base mandatory check
    let clearCIMandatory = this.incomeMonthly != null && this.incomeInvRate != null;

    if (validateCINeed) {
      clearCIMandatory = clearCIMandatory && this.yearsRequired != null;
    }

    clearCIMandatory = clearCIMandatory && this.isOtherFieldsValidated && this.isResourcesOtherFieldsValidated;

    const hasValidationError = (reasonStatus, reason, reasonCR) =>
      (reasonStatus && !reason) || (reason && !reasonCR);

    const incomeMonthlyError = hasValidationError(
      this.incomeMonthlyReasonStatus,
      this.incomeMonthlyReason,
      this.incomeMonthlyCR
    );

    const incomeInvRateError = hasValidationError(
      this.incomeInvRateReasonStatus,
      this.incomeInvRateReason,
      this.incomeInvRateCR
    );

    const incomeAssumedInfRateError = hasValidationError(
      this.incomeAssumedInfRateReasonStatus,
      this.incomeAssumedInfRateReason,
      this.incomeAssumedInfRateCR
    );

    const incomeInfAdjRateError = hasValidationError(
      this.incomeInfAdjRateReasonStatus,
      this.incomeInfAdjRateReason,
      this.incomeInfAdjRateCR
    );

    const exResourcesLICError = hasValidationError(
      this.exResourcesLICReasonStatus,
      this.exResourcesLICReason,
      this.exResourcesLICCR
    );

    const exResourcesCAError = hasValidationError(
      this.exResourcesCAReasonStatus,
      this.exResourcesCAReason,
      this.exResourcesCACR
    );

    const reasonValidationErrors = [
      incomeMonthlyError,
      incomeInvRateError,
      incomeAssumedInfRateError,
      incomeInfAdjRateError,
      exResourcesLICError,
      exResourcesCAError,
    ];

    if (validateCINeed) {
      const yearsRequiredError = hasValidationError(
        this.yearsRequiredReasonStatus,
        this.yearsRequiredReason,
        this.yearsRequiredCR
      );
      reasonValidationErrors.push(yearsRequiredError);
    }

    const hasReasonValidationError = reasonValidationErrors.some(Boolean) || !!this.exResourcesLICError;

    return clearCIMandatory && !hasReasonValidationError && !this.needRevisit;
  }


  // Justification Sections
  get incomeMonthlyReasonStatus () {
    if (!this.selectedClient) return
    let isNeedReason = false
    const selectedClientByCashFlow = this.selectedClient?.cashFlow

    if (selectedClientByCashFlow?.isUserDeclinedCashFlow === true)  {
      this.incomeMonthlyReason = undefined
      return false
    }
    
    const higherSimplifiedTotalMonthlyAmount = Math.max(selectedClientByCashFlow?.simplifiedTotalInflow, selectedClientByCashFlow?.simplifiedTotalOutflow) || null
    const higherComprehensiveTotalMonthlyAmount = Math.max(selectedClientByCashFlow?.totalMonthlyOutflow, selectedClientByCashFlow?.netEmploymentIncome) || null
    const highestIncome = selectedClientByCashFlow?.isSimplifiedMode ? higherSimplifiedTotalMonthlyAmount : higherComprehensiveTotalMonthlyAmount
    
    if (highestIncome < this.incomeMonthly){
      isNeedReason = true
      if (this.cacheIncomeMonthly !== this.incomeMonthly) {
        this.cacheIncomeMonthly = this.incomeMonthly
        this.incomeMonthlyCR = false
      }
    } else {
      this.incomeMonthlyReason = undefined
      this.cacheIncomeMonthly = this.incomeMonthly
      this.incomeMonthlyCR = false
    }

    return isNeedReason
  }

  get yearsRequiredReasonStatus () {
    if (!this.selectedClient) return
    let isNeedReason = false

    if (this.yearsRequired !== this.CRITICAL_ILLNESS_YEARS_REQ){
      isNeedReason = true
      if (this.cachedYearsRequired !== this.yearsRequired) {
        this.cachedYearsRequired = this.yearsRequired
        this.yearsRequiredCR = false
      }
    } else {
      this.yearsRequiredReason = undefined
      this.cachedYearsRequired = this.yearsRequired
      this.yearsRequiredCR = false
    }

    return isNeedReason
  }

  get incomeInvRateReasonStatus () {
    if (!this.selectedClient) return
    let isNeedReason = false

    if (this.incomeInvRate < 0 || this.incomeInvRate > Global.MAX_INTEREST_RATE) {
      isNeedReason = true
      if (this.cachedIncomeInvRate !== this.incomeInvRate) {
        this.cachedIncomeInvRate = this.incomeInvRate
        this.incomeInvRateCR = false
      }
    } else {
      this.incomeInvRateReason = undefined
      this.cachedIncomeInvRate = this.incomeInvRate
      this.incomeInvRateCR = false
    }

    return isNeedReason
  }

  get incomeAssumedInfRateReasonStatus () {
    if (!this.selectedClient) return
    let isNeedReason = false

    if (this.incomeAssumedInfRate < 0 || this.incomeAssumedInfRate > Global.MAX_INTEREST_RATE) {
      isNeedReason = true
      if (this.cachedIncomeAssumedInfRate !== this.incomeAssumedInfRate) {
        this.cachedIncomeAssumedInfRate = this.incomeAssumedInfRate
        this.incomeAssumedInfRateCR = false
      }
    } else {
      this.incomeAssumedInfRateReason = undefined
      this.cachedIncomeAssumedInfRate = this.incomeAssumedInfRate
      this.incomeAssumedInfRateCR = false
    }

    return isNeedReason
  }

  get incomeInfAdjRateReasonStatus() {
    if (!this.selectedClient) return
    let isNeedReason = false

    if (this.incomeInfAdjRate < -2 || this.incomeInfAdjRate > 9) {
      isNeedReason = true
      if (this.cachedIncomeInfAdjRate !== this.incomeInfAdjRate) {
        this.cachedIncomeInfAdjRate = this.incomeInfAdjRate
        this.incomeInfAdjRateCR = false
      }
    } else {
      this.incomeInfAdjRateReason = undefined
      this.cachedIncomeInfAdjRate = this.incomeInfAdjRate
      this.incomeInfAdjRateCR = false
    }

    return isNeedReason
  }

  get exResourcesLICReasonStatus() {
    if (!this.selectedClient) return
    let isNeedReason = false
    const selectedClientByExInsurance = this.selectedClient?.existingInsurence;
      let calcTotal = 0;
      // Sum sumAssuredCI in client's insuranceRecords
      if (selectedClientByExInsurance?.insuranceRecords) {
        calcTotal += selectedClientByExInsurance.insuranceRecords.reduce((sum, record) => sum + (record.sumAssuredCI || 0), 0);
      }
    
    if (this.exResourcesLIC < calcTotal ){
      isNeedReason = true
      if (this.exResourcesLICReason && calcTotal !== this.cachedxResourcesLIC && this.exResourcesLIC !== this.cachedxResourcesLIC) {
        this.exResourcesLICCR = false
      }
    } else {
      this.exResourcesLICReason = undefined
      this.exResourcesLICCR = false
    }

    return isNeedReason
  }

  get exResourcesLICError () {
    if (!this.selectedClient) return
    const selectedClientByExInsurance = this.selectedClient?.existingInsurence;
      let calcTotal = 0;
      // Sum sumAssuredCI in client's insuranceRecords
      if (selectedClientByExInsurance?.insuranceRecords && selectedClientByExInsurance?.discloseExistingInsurance !== true) {
        calcTotal += selectedClientByExInsurance.insuranceRecords.reduce((sum, record) => sum + (record.sumAssuredCI || 0), 0);
      }
    
    if (this.exResourcesLIC > calcTotal ){
      return NA_ERROR_MSG.TB
    } else {
      return null
    }
  }

  get exResourcesCAReasonStatus() {
    if (!this.selectedClient) return
    let isNeedReason = false
    const selectedClientByNetWorth= this.selectedClient?.netWorth
      
    const calcTotal = selectedClientByNetWorth?.isSimplifiedMode ? selectedClientByNetWorth?.simplifiedTotalCash : sum(selectedClientByNetWorth?.savings,
      selectedClientByNetWorth?.fixedDeposits,
      selectedClientByNetWorth?.cashNearCashOthersAmount
    ) || 0

    // if (
    //   this.exResourcesCA === undefined ||
    //   this.cachedExResourcesCAAuto !== calcTotal
    // ) {
    //   this.exResourcesCA = calcTotal || undefined
    //   this.cachedExResourcesCAAuto = calcTotal
    // }
    
    const isZeroAndUndefined = calcTotal === 0 && (this.exResourcesCA === undefined ||this.exResourcesCA === null);
    if (!isZeroAndUndefined) {
      if (this.exResourcesCA !== calcTotal){
        isNeedReason = true
        if (this.cachedExResourcesCA !== this.exResourcesCA) {
          this.cachedExResourcesCA = this.exResourcesCA
          this.exResourcesCACR = false
        }
      } else {
        this.exResourcesCAReason = undefined
        this.cachedExResourcesCA = this.exResourcesCA
        this.exResourcesCACR = false
      }
    }
    return isNeedReason
  }

  resetAllFields (): void {
    super.resetAllFields()
  }
}